import { useState } from 'react'
import { FileArchive, FileUp, FileOutput } from 'lucide-react'
import { Seo } from '@/components/seo'
import { PageLayout } from '@/components/layout/page-layout'
import { ToolPageLayout } from '@/components/tools/tool-page-layout'
import { cn } from '@/lib/utils'
import { CompressPanel } from '@/features/zip/compress-panel'
import { ExtractPanel } from '@/features/zip/extract-panel'
import { useI18n } from '@/lib/i18n'

type ZipMode = 'compress' | 'extract'

const MODES = [
  { id: 'compress', labelKey: 'modeCompress' as const, icon: FileUp },
  { id: 'extract', labelKey: 'modeExtract' as const, icon: FileOutput },
] as const satisfies ReadonlyArray<{
  id: ZipMode
  labelKey: 'modeCompress' | 'modeExtract'
  icon: typeof FileUp
}>

export default function ZipToolsPage() {
  const [mode, setMode] = useState<ZipMode>('compress')
  const { t } = useI18n()

  return (
    <PageLayout>
      <Seo
        title={t('seoZipTitle')}
        description={t('seoZipDesc')}
        keywords={t('seoZipKw').split(',').map((item) => item.trim())}
      />
      <ToolPageLayout
        icon={FileArchive}
        title={t('toolPageZip')}
        description={t('toolPageZipDesc')}
        formats={['ZIP']}
      >
        <div className="grid grid-cols-2 gap-1 rounded-xl border bg-card p-1 shadow-sm">
          {MODES.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setMode(item.id)}
              className={cn(
                'flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors duration-150',
                mode === item.id
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <item.icon className="h-4 w-4" aria-hidden />
              {t(item.labelKey)}
            </button>
          ))}
        </div>

        {mode === 'compress' ? <CompressPanel /> : <ExtractPanel />}
      </ToolPageLayout>
    </PageLayout>
  )
}